import * as React from 'react';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { FILTERS_CHANGED_EVENT, FILTER_STORAGE_KEYS, TIME_PERIODS } from 'bundles/enterprise-admin-home/constants';

export type EnterpriseAdminCoachProps = {
  currentTimePeriod: string;
  contractIds: string[];
  programIds: string[];
};

export const EnterpriseAdminCoachContext = React.createContext<EnterpriseAdminCoachProps>({
  currentTimePeriod: '',
  contractIds: [],
  programIds: [],
});

const DEFAULT_TIME_PERIOD = TIME_PERIODS.LAST_90_DAYS;

export const EnterpriseAdminCoachProvider = ({ children }: React.PropsWithChildren) => {
  const [currentTimePeriod, setCurrentTimePeriod] = React.useState(() => {
    try {
      return localStorageEx.getItem(FILTER_STORAGE_KEYS.TIME_PERIOD, JSON.parse, DEFAULT_TIME_PERIOD);
    } catch {
      return DEFAULT_TIME_PERIOD;
    }
  });

  const [contractIds, setContractIds] = React.useState<string[]>(() => {
    try {
      return localStorageEx.getItem(FILTER_STORAGE_KEYS.CONTRACTS, JSON.parse, []);
    } catch {
      return [];
    }
  });

  const [programIds, setProgramIds] = React.useState<string[]>(() => {
    try {
      return localStorageEx.getItem(FILTER_STORAGE_KEYS.PROGRAMS, JSON.parse, []);
    } catch {
      return [];
    }
  });

  // Listen for filter changes via custom event
  React.useEffect(() => {
    const handleFiltersChanged = () => {
      try {
        const newContractIds = localStorageEx.getItem(FILTER_STORAGE_KEYS.CONTRACTS, JSON.parse, []);
        const newProgramIds = localStorageEx.getItem(FILTER_STORAGE_KEYS.PROGRAMS, JSON.parse, []);
        const newTimePeriod = localStorageEx.getItem(FILTER_STORAGE_KEYS.TIME_PERIOD, JSON.parse, DEFAULT_TIME_PERIOD);
        setContractIds(newContractIds);
        setProgramIds(newProgramIds);
        setCurrentTimePeriod(newTimePeriod);
      } catch {
        // Do nothing
      }
    };

    window.addEventListener(FILTERS_CHANGED_EVENT, handleFiltersChanged);

    return () => {
      window.removeEventListener(FILTERS_CHANGED_EVENT, handleFiltersChanged);
    };
  }, []);

  const contextValue = React.useMemo(
    () => ({
      currentTimePeriod,
      contractIds,
      programIds,
    }),
    [currentTimePeriod, contractIds, programIds]
  );

  return <EnterpriseAdminCoachContext.Provider value={contextValue}>{children}</EnterpriseAdminCoachContext.Provider>;
};

export const useEnterpriseAdminCoach = () => React.useContext(EnterpriseAdminCoachContext);
